import PropTypes from 'prop-types';
import { Grid, IconButton, MenuItem, Paper, TextField, Tooltip } from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { slugify } from './filterConfig';

// Controlled list of { value, label } rows for a filter's options.
// `valueChoices` (e.g. sort keys) turns the value input into a fixed select.
const OptionsEditor = ({ options, onChange, valueChoices, helperText }) => {
  const update = (index, patch) =>
    onChange(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));

  const handleLabel = (index, label) => {
    const option = options[index];
    // Keep the value in step with the label until the admin edits the value by hand
    const autoValue = !option.value || option.value === slugify(option.label);
    update(index, valueChoices || !autoValue ? { label } : { label, value: slugify(label) });
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        {helperText}
      </MDTypography>

      {options.map((option, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Label"
                value={option.label}
                onChange={(e) => handleLabel(index, e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {valueChoices ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Value"
                  value={option.value}
                  onChange={(e) => update(index, { value: e.target.value })}
                  SelectProps={{ sx: { height: 40 } }}
                >
                  {valueChoices.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Value"
                  value={option.value}
                  onChange={(e) => update(index, { value: e.target.value })}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={3} display="flex" justifyContent="flex-end">
              <Tooltip title="Move up">
                <span>
                  <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                    <Icon fontSize="small">arrow_upward</Icon>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === options.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <Icon fontSize="small">arrow_downward</Icon>
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onChange(options.filter((_, i) => i !== index))}
                >
                  <Icon fontSize="small">delete</Icon>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={() => onChange([...options, { value: '', label: '' }])}
      >
        <Icon sx={{ mr: 0.5 }}>add</Icon> Add option
      </MDButton>
    </MDBox>
  );
};

OptionsEditor.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
  valueChoices: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  helperText: PropTypes.string,
};

OptionsEditor.defaultProps = {
  valueChoices: null,
  helperText: 'Label is what shoppers see; value is what gets saved on the value pack.',
};

export default OptionsEditor;
